import { useNavigate } from 'react-router';

import { IconX } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { MouseEventHandler } from 'react';
import { Trans } from 'react-i18next';

import { Link } from '@cfa/react-components';
import { MessageBlock } from '@cfa/react-core';

import { useAuth } from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';

import { env } from '@/env';

const dismissedUserIdsAtom = atomWithStorage<string[]>(
  'managePlansEmbedVideoBannerDismissals',
  [],
  undefined,
  { getOnInit: true },
);

const docId =
  env.REACT_APP_ENV === 'prod'
    ? 'crn:pathway:resource:01K9TJ1NCDA1K6XHTHHC7QQS8A'
    : env.REACT_APP_ENV === 'dev'
      ? 'crn:pathway:resource:01JRJZFBYN105S9VE14F41SZT6'
      : 'crn:pathway:resource:01K9TJ1P8M7M417ZKBY0SRYVY8';

// Params come from the link content owners published in PATHWAY-3862: they set
// the document header back target to Recent Updates and attribute ResourceView
// to that category. Without `from`, routedFrom defaults to 'link' and back
// sends the user to the home page instead.
const learnMoreUrl = `/doc/${docId}?${new URLSearchParams({
  from: 'browse',
  backUrl: '/category/recent-updates',
  cat: 'Recent Updates',
  sub: 'Recently Updated Features',
  // Opens Choose Document on SPA Push when the resource has multiple versions
  // (useDocument otherwise suppresses the chooser for in-app navigation).
  chooseVersion: 'true',
})}`;

const ManagePlansEmbedVideoBanner = () => {
  const { data: auth } = useAuth();
  const { managePlansEmbedVideoBanner } = useFlags();
  const navigate = useNavigate();
  const userId = auth?.userId ?? '';
  const [dismissedUserIds, setDismissedUserIds] = useAtom(dismissedUserIdsAtom);

  const hasDismissed = dismissedUserIds.includes(userId);

  const dismiss = () => {
    if (!userId || hasDismissed) return;
    setDismissedUserIds(prev => [...prev, userId]);
  };

  const dismissAndNavigateToDoc: MouseEventHandler = event => {
    event.preventDefault();
    dismiss();
    navigate(learnMoreUrl);
  };

  if (!managePlansEmbedVideoBanner || hasDismissed) {
    return null;
  }

  return (
    <div className="mb-4" data-testid="ManagePlansEmbedVideoBanner">
      <MessageBlock.Root severity="info" className="text-start!">
        <div className="flex w-full items-start justify-between gap-2">
          <p className="m-0">
            <Trans
              i18nKey="TrainingPlans.embedVideoBannerBody"
              components={{
                learnMoreLink: (
                  <Link
                    href={learnMoreUrl}
                    onClick={dismissAndNavigateToDoc}
                    variant="inline"
                  />
                ),
              }}
            />
          </p>
          <button
            type="button"
            className="flex shrink-0 items-start justify-center p-1"
            aria-label="Dismiss"
            onClick={dismiss}
          >
            <IconX size={20} className="text-grayscale-gray5 cursor-pointer" />
          </button>
        </div>
      </MessageBlock.Root>
    </div>
  );
};

export default ManagePlansEmbedVideoBanner;
